import { borrarPreferencia, guardarPreferencia, leerPreferencia } from '@vigilancia/cascaron';

import { esDocumento } from './campos';

/**
 * EL USUARIO RECORDADO · lo único de una persona que esta puerta guarda.
 *
 * Es **el documento y nada más**. La contraseña no se guarda, no se cifra, no
 * se ofusca: no se guarda. Y la sesión tampoco — «recordar usuario» ahorra
 * escribir un número, no mantiene a nadie dentro.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * POR QUÉ ESTO ES UNA DECISIÓN Y NO UNA COMODIDAD, y está escrita en el
 * ADR-0013 del frontend.
 *
 * Un documento de identidad **es un dato de una persona**, y el almacenamiento
 * del navegador sobrevive al cierre de sesión: en el equipo donde se marque,
 * quien lo use después verá ese número en el campo. En el caso normal de este
 * sistema —el dispositivo propio de quien entra— no hay «el siguiente», y donde
 * lo haya corre por cuenta de quien lo usa; y no da acceso a nada, porque sin
 * la contraseña no se entra. Aun así, y en este orden:
 *
 *   1. **Nadie recuerda a nadie sin pedirlo.** La casilla nace apagada y sólo
 *      se guarda al enviar el formulario con ella marcada.
 *   2. **Apagarla olvida en el acto**, sin esperar a ningún envío. Quitar la
 *      marca ES el gesto de borrar.
 *   3. **Se guarda lo que tiene forma de documento y nada más.** Lo que se lee
 *      del almacén entra por la misma puerta: un valor que no cumple la forma
 *      se descarta, porque el almacenamiento del navegador lo escribe cualquier
 *      guion del mismo origen y se edita a mano desde las herramientas.
 *   4. **No viaja al servidor.** No hay campo en el formulario ni cookie: el
 *      número se queda en este equipo. Lo que el servidor recibe es lo mismo
 *      que recibía antes de que esto existiera.
 *
 * ⚠️ **Y por eso no se registra ni se traza**: un documento es de alguien.
 *
 * La guarda del almacén —que puede estar bloqueado y **lanzar al leerlo**— la
 * pone el armazón, que es donde está escrita una sola vez.
 */

/**
 * La clave, con el prefijo del sistema para no chocar con nada del mismo
 * origen, y con el subsistema dentro para que se vea de quién es.
 */
export const CLAVE_USUARIO_RECORDADO = 'nat-acceso-usuario';

/**
 * El usuario recordado en ESTE navegador, o la cadena vacía si no hay ninguno
 * —o si lo que hay no tiene forma de documento—.
 */
export function leerUsuarioRecordado(): string {
  const guardado = leerPreferencia(CLAVE_USUARIO_RECORDADO);
  if (guardado === null) return '';
  const limpio = guardado.trim();
  return esDocumento(limpio) ? limpio : '';
}

/**
 * Recuerda un documento en este navegador.
 *
 * ⚠️ **Lo que no tiene forma de documento OLVIDA en vez de guardar.** No es un
 * descarte silencioso: si alguien envía el formulario con la casilla marcada y
 * el campo a medio escribir, lo recordado deja de corresponder con lo último
 * que esa persona quiso recordar, y un recuerdo viejo es peor que ninguno.
 */
export function recordarUsuario(documento: string): void {
  const limpio = documento.trim();
  if (!esDocumento(limpio)) {
    olvidarUsuario();
    return;
  }
  guardarPreferencia(CLAVE_USUARIO_RECORDADO, limpio);
}

/** Olvida el usuario recordado. Llamarla sin nada guardado no hace nada. */
export function olvidarUsuario(): void {
  borrarPreferencia(CLAVE_USUARIO_RECORDADO);
}

/**
 * Si el campo lo rellenó el NAVEGADOR y no nosotros.
 *
 * 🔴 **EXISTE PARA NO PISAR EL GESTOR DE CONTRASEÑAS.** Chrome rellena usuario
 * y contraseña a la vez, y mientras están en ese estado **esconde el valor de
 * JavaScript**: `input.value` devuelve la cadena vacía aunque en pantalla se
 * vea el documento —medido el 23/09/2026—. Escribir encima no sólo tacharía lo
 * suyo por lo nuestro: al tocar el campo del usuario, el navegador **suelta
 * también la contraseña que había puesto**, y quien entra con su gestor se
 * quedaría con el segundo campo vacío sin entender por qué.
 *
 * Dos selectores porque son dos nombres de lo mismo: `:autofill` es el
 * estándar y `:-webkit-autofill` el que entienden los navegadores que todavía
 * no lo implementan con su nombre. Van en `try` porque un selector que el
 * navegador no conoce **lanza** en `matches()`, y eso tumbaría el arranque de
 * la pantalla por una comodidad.
 */
export function loRellenoElNavegador(campo: Element): boolean {
  for (const selector of [':autofill', ':-webkit-autofill']) {
    try {
      if (campo.matches(selector)) return true;
    } catch {
      // Ese nombre no lo conoce; queda el otro.
    }
  }
  return false;
}
